import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import type { Product, Category } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CatalogoPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [sortBy, setSortBy] = useState('featured');
  const [buttonStates, setButtonStates] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsResult, categoriesResult] = await Promise.all([
        supabase.from('products').select('*').eq('active', true),
        supabase.from('categories').select('*').order('name')
      ]);

      if (!productsResult.error && productsResult.data) {
        setProducts(productsResult.data);
      }

      if (!categoriesResult.error && categoriesResult.data) {
        setCategories(categoriesResult.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: Product) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find((item: any) => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.images[0] || '',
        quantity: 1,
      });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));

    setButtonStates({ ...buttonStates, [product.id]: true });
    setTimeout(() => {
      setButtonStates({ ...buttonStates, [product.id]: false });
      window.REACT_APP_NAVIGATE('/cart');
    }, 500);
  };

  const filteredProducts = products
    .filter((product) => {
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];
      return matchesCategory && matchesSearch && matchesPrice;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc':
          return a.price - b.price;
        case 'price-desc':
          return b.price - a.price;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'featured':
        default:
          return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      }
    });

  return (
    <div className="min-h-screen bg-gray-900">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-4xl font-black text-white mb-4">
              Catálogo <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-cyan-600">Completo</span>
            </h1>
            <p className="text-gray-400 text-lg">Encontre os melhores componentes para seu setup</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-64 space-y-6">
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <i className="ri-search-line text-neon-cyan"></i>
                  Buscar
                </h3>
                <input
                  type="text"
                  placeholder="Buscar produtos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-neon-cyan focus:outline-none text-sm"
                />
              </div>

              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <i className="ri-filter-3-line text-neon-cyan"></i>
                  Categorias
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                      selectedCategory === 'all'
                        ? 'bg-gradient-to-r from-pink-600 to-cyan-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    Todos os Produtos
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.name)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                        selectedCategory === category.name
                          ? 'bg-gradient-to-r from-pink-600 to-cyan-600 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                    >
                      <i className={`${category.icon} mr-2`}></i>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <i className="ri-money-dollar-circle-line text-neon-cyan"></i>
                  Faixa de Preço
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">R$ {priceRange[0]}</span>
                    <span className="text-gray-400">R$ {priceRange[1]}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="10000"
                    step="100"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full cursor-pointer"
                  />
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <i className="ri-sort-desc text-neon-cyan"></i>
                  Ordenar Por
                </h3>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-neon-cyan focus:outline-none text-sm cursor-pointer"
                >
                  <option value="featured">Destaques</option>
                  <option value="price-asc">Menor Preço</option>
                  <option value="price-desc">Maior Preço</option>
                  <option value="name">Nome A-Z</option>
                </select>
              </div>
            </aside>

            <div className="flex-1">
              {loading ? (
                <div className="text-center py-20">
                  <i className="ri-loader-4-line text-4xl text-neon-cyan animate-spin"></i>
                  <p className="mt-4 text-gray-400">Carregando produtos...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-shopping-bag-line text-6xl text-gray-600 mb-4"></i>
                  <p className="text-gray-400 text-lg">Nenhum produto encontrado</p>
                </div>
              ) : (
                <>
                  <div className="mb-6 flex items-center justify-between">
                    <p className="text-gray-400">
                      Mostrando <span className="text-white font-semibold">{filteredProducts.length}</span> produtos
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-pink-600 transition-all group"
                      >
                        <div className="relative h-64 w-full overflow-hidden bg-gray-900">
                          <img
                            src={product.images[0] || 'https://public.readdy.ai/ai/img_res/e44ad9009ca3bfc3e00a733f3aeda182.jpg'}
                            alt={product.name}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                          />
                          {product.old_price && product.old_price > product.price && (
                            <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-600 to-cyan-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                              -{Math.round(((product.old_price - product.price) / product.old_price) * 100)}%
                            </div>
                          )}
                          {product.stock <= 5 && product.stock > 0 && (
                            <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                              Últimas unidades!
                            </div>
                          )}
                        </div>

                        <div className="p-6">
                          <h3 className="text-white font-bold text-lg mb-2 line-clamp-2">{product.name}</h3>
                          {product.brand && (
                            <p className="text-gray-400 text-sm mb-3">{product.brand}</p>
                          )}
                          
                          <div className="flex items-center gap-2 mb-4">
                            <div className="flex text-yellow-400">
                              {[...Array(5)].map((_, i) => (
                                <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
                              ))}
                            </div>
                            <span className="text-gray-400 text-sm">({product.reviews})</span>
                          </div>

                          <div className="mb-4">
                            {product.old_price && product.old_price > product.price && (
                              <p className="text-gray-500 line-through text-sm">
                                R$ {product.old_price.toFixed(2)}
                              </p>
                            )}
                            <p className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-cyan-600 text-2xl font-black">
                              R$ {product.price.toFixed(2)}
                            </p>
                          </div>

                          <button
                            onClick={() => addToCart(product)}
                            disabled={product.stock === 0}
                            className={`w-full py-3 rounded-lg font-black transition-all shadow-lg whitespace-nowrap cursor-pointer ${
                              product.stock === 0
                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 hover:from-pink-500 hover:via-purple-500 hover:to-cyan-500 text-white shadow-pink-500/50 hover:shadow-pink-500/70'
                            }`}
                          >
                            {product.stock === 0 ? 'Fora de Estoque' : buttonStates[product.id] ? 'Adicionado!' : 'Adicionar ao Carrinho'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
